import Definition from '@client/config/definition';
import { i18n } from '@client/helper/i18n';

import * as corosSportConfig from '@coros/sport-config';
import { Ref } from 'vue';
import { SportMode } from '@mytypes/activity-detail/ApiModel/SportMode';
import type { LapItem } from '@mytypes/activity-detail/LapList';
import { newLapListType } from '../hooks/useLapSelectValue';

const { isIndoorClimbOrBoulderingTrainingSportName } = corosSportConfig.sport.climb;

const { t } = i18n;

type Option = {
  isProgram: boolean;
  sportMode: SportMode;
  data: LapItem;
  sportName: string;
  index: number;
};

type Inject = {
  isAllDistanceRef: Ref<boolean>;
  hasPostureTest?: Ref<boolean>;
};

/* 圈数表圈数名相关begin-------------------------------> */
// 获取圈速表中数据的名称
export function getActivityName(option: Option, inject: Inject) {
  const { isProgram = false, sportMode, sportName, data } = option;
  const { isAllDistanceRef } = inject;

  if (isAllDistanceRef.value) {
    return '';
  }

  // console.log(`getActivityIndex`, data?.uniqueId, isProgram, sportMode, data?.gradingSystem);
  // console.log(`getActivityIndex`, data?.uniqueId, isProgram, 'mode', data.mode, 'setindex', data.setIndex);
  if (isProgram || (newLapListType.value && sportMode === SportMode.LACTATE_THRESHOLD)) {
    // 训练课程
    return getNameForProgramSport(option);
  } else if (sportMode === SportMode.INTERVAL) {
    // 间歇训练
    return getNameForIntervalSport(option);
  }
  // 普通运动
  return getNameForBasicSport(option, inject);
}

// 获取训练组合详情的圈速表名称
export function getNameForProgramSport(option: Option) {
  const { data, sportName = '' } = option;
  const lapListTypeIsLap = newLapListType.value;

  if (data.indexInOriginLap) {
    return '';
  }

  // 健身
  if (['Strength'].includes(sportName)) {
    // console.log(`getNameForProgramSport`, lapListTypeIsLap, data.uniqueId, data.mode);
    if (lapListTypeIsLap && !Definition.isExerciseMode(data.mode)) {
      return Definition.getModeI18nKey(data.mode);
    }
    // 健身动作
    return data.exerciseNameKey || '';
  }

  // 室内攀岩、抱石
  if (isIndoorClimbOrBoulderingTrainingSportName(sportName)) {
    if (data.setIndex > 1) return '';
    // 所有运动改为显示运动类型名称
    if (Definition.isExerciseMode(data.mode)) {
      return Definition.getSportI18nKeyBySportType(data.sportType);
    }
    if (lapListTypeIsLap) {
      return Definition.getModeI18nKey(data.mode);
    }
    return data.exerciseNameKey || '';
  }

  let result = '';
  result = Definition.getModeI18nKey(data.mode);
  // 2024年06月26日13:49:38 根据测试提供的信息 所有运动不使用下面的逻辑
  // if (Definition.isExerciseMode(data.mode)) {
  //   result = Definition.getSportI18nKeyBySportType(data.sportType);
  // }
  if (data.mode === 25) {
    result = Definition.getSportI18nKeyBySportType(data.sportType);
  }
  // 所有运动改为显示运动类型名称
  if (Definition.isExerciseMode(data.mode)) {
    result = Definition.getSportI18nKeyBySportType(data.sportType);
  }
  return result;
}

// 获取间歇运动详情的圈速表名称
export function getNameForIntervalSport(option: Option) {
  const { data, sportName = '' } = option;
  const { exerciseType, lapType } = data;
  if (data.indexInOriginLap > 0) return '';

  // 健身
  if (['Strength'].includes(sportName)) {
    // 健身动作
    return data.exerciseNameKey || '';
  }
  // 特殊逻辑 跳绳 Jump_Rope 901
  if (['Jump_Rope'].includes(sportName)) {
    // 热身
    if (exerciseType === 1) {
      return 'T1120';
    } else if (exerciseType === 3) {
      // 放松
      return 'T1122';
    } else if (exerciseType === 2) {
      if (data.mode === 17) {
        // 休息
        return 'H6035';
      } else if (Definition.isExerciseMode(data.mode)) {
        return t(Definition.getSportI18nKeyBySportName(sportName));
      }
      return Definition.getModeI18nKey(data.mode);
    }
  }

  // console.log(`getNameForIntervalSport`, data.uniqueId, sportName, data.mode);
  if (Definition.isExerciseMode(data.mode)) {
    return t(Definition.getSportI18nKeyBySportName(sportName));
  }

  return Definition.getModeI18nKey(data.mode);
}

// 获取普通运动详情的圈速表名称
export function getNameForBasicSport(option: Option, inject: Inject) {
  const { data, sportName = '' } = option;
  const { hasPostureTest } = inject;

  // 健身
  if (['Strength'].includes(sportName)) {
    // 健身动作
    return data.exerciseNameKey || '';
  }

  // 登山滑雪(新)
  if (['Ski_Touring'].includes(sportName)) {
    if (![3, 4].includes(data.lapListType)) {
      if (!data.sectionIndex || ![1, '1'].includes(data.rowIndex)) {
        return '';
      }
    }
    return (
      {
        1: 'H4015',
        2: 'H4014',
        3: 'H4015',
        4: 'H4014'
      }[data.lapType] || ''
    );
  }

  // 登山滑雪(旧)
  if (['Ski_Touring_Old'].includes(sportName)) {
    return (
      {
        // 登山
        Mtn_Climb: 'H4015',
        // 越野滑雪
        XC_Ski: 'H4014'
      }[Definition.getSportNameByType(data.sportType)] || ''
    );
  }

  // 多段攀登
  if (['Multi_Pitch'].includes(sportName)) {
    return (
      {
        // 接近
        0: 'H2027',
        // 攀登
        1: 'H2026',
        // 下撤
        2: 'H2028'
      }[data.mutliPitchStage] || ''
    );
  }

  // 攀登
  if (['Climb'].includes(sportName)) {
    return (
      {
        // 接近
        0: 'H2027',
        // 攀登
        1: 'H2026',
        // 下撤
        2: 'H2028'
      }[data.mutliPitchStage] || ''
    );
  }

  // 组合运动、铁人三项
  if (['Multi_Sport', 'Triathlon'].includes(sportName)) {
    return Definition.getSportI18nKeyBySportType(data.sportType);
  }

  // 跳绳
  if (['Jump_Rope'].includes(sportName)) {
    if (Definition.isRestMode(data.mode)) {
      return Definition.getModeI18nKey(data.mode);
    }
  }

  // 跑姿
  if (!!hasPostureTest?.value && ['Run', 'Track_Run'].includes(sportName)) {
    if (Definition.isWarmupMode(data.mode) || Definition.isTestExerciseMode(data.mode)) {
      return Definition.getModeI18nKey(data.mode);
    }
    if (data.mode === 25) {
      return Definition.getSportI18nKeyBySportName(sportName);
    }
  }

  return '';
}
/* 圈数表圈数名相关end-------------------------------> */
